/**
 * BrandLogoUpload Component
 *
 * Upload, preview and removal for one logo slot.
 *
 * Validates the file here as well as in the hook. That is not duplicated logic —
 * both call the same `validatePng` — but it is a deliberate second call: the
 * component owns the file input and the error display, so rejecting here gives
 * immediate feedback without a round trip through the hook's state.
 *
 * Requirements: 8.1, 8.2, 8.6, 8.7, 8.9, 8.10, 8.12, 9.3
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  EDITABLE_ASSET_SLOT_INFO,
  type BrandAsset,
  type EditableAssetSlot,
} from '../models/domainModels';
import {
  describePngRejection,
  MAX_ASSET_BYTES,
  MAX_ASSET_PIXELS,
  validatePng,
} from '../utils/pngValidation';
import './BrandLogoUpload.css';

export interface BrandLogoUploadProps {
  readonly slot: EditableAssetSlot;
  readonly asset: BrandAsset | null;
  readonly onUpload: (file: File, alt: string) => void;
  readonly onRemove: () => void;
}

export function BrandLogoUpload({
  slot,
  asset,
  onUpload,
  onRemove,
}: BrandLogoUploadProps): JSX.Element {
  const fieldId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [alt, setAlt] = useState(asset?.alt ?? '');
  const [rejection, setRejection] = useState<string | null>(null);
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);

  const { label, description } = EDITABLE_ASSET_SLOT_INFO[slot];

  // Adopt the stored alt text when the asset changes, so a re-upload keeps the
  // description the club already approved rather than starting blank.
  useEffect(() => {
    if (asset !== null) setAlt(asset.alt);
  }, [asset]);

  const trimmedAlt = alt.trim();
  const canChooseFile = trimmedAlt.length > 0;

  const handleChooseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset so choosing the same file again re-triggers the change event.
      event.target.value = '';
      if (!file) return;

      setRejection(null);

      const bytes = new Uint8Array(await file.arrayBuffer());
      const reason = describePngRejection(validatePng(bytes));
      if (reason !== null) {
        setRejection(reason);
        return;
      }

      onUpload(file, trimmedAlt);
    },
    [onUpload, trimmedAlt]
  );

  const handleRemoveConfirmed = useCallback(() => {
    setIsConfirmingRemoval(false);
    onRemove();
  }, [onRemove]);

  return (
    <section className="brand-logo">
      <h3 className="brand-logo__label">{label}</h3>
      <p className="brand-logo__description">{description}</p>

      {asset !== null && (
        <div className="brand-logo__preview">
          <img className="brand-logo__image" src={asset.url} alt={asset.alt} />
          <span className="brand-logo__dimensions">
            {asset.widthPx} × {asset.heightPx} px
          </span>
        </div>
      )}

      <div className="brand-logo__alt">
        <label className="brand-logo__alt-label" htmlFor={fieldId}>
          Alt text
        </label>
        <input
          id={fieldId}
          type="text"
          className="brand-logo__alt-input"
          value={alt}
          onChange={(event) => setAlt(event.target.value)}
          placeholder="Describe the logo"
          aria-label="Alt text"
          aria-describedby={`${fieldId}-alt-hint`}
        />
        <p id={`${fieldId}-alt-hint`} className="brand-logo__hint">
          Describes the logo for players using a screen reader. Required.
        </p>
      </div>

      <div className="brand-logo__actions">
        <button
          type="button"
          className="brand-logo__button brand-logo__button--primary"
          onClick={handleChooseClick}
          disabled={!canChooseFile}
        >
          {asset === null ? 'Choose PNG' : 'Replace PNG'}
        </button>

        {asset !== null && !isConfirmingRemoval && (
          <button
            type="button"
            className="brand-logo__button"
            onClick={() => setIsConfirmingRemoval(true)}
          >
            Remove
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="brand-logo__file-input"
        accept=".png,image/png"
        onChange={handleFileChange}
        tabIndex={-1}
        aria-hidden="true"
      />

      {isConfirmingRemoval && (
        <div className="brand-logo__confirm">
          <p className="brand-logo__confirm-text">
            Remove this logo? It stays visible to players until you publish.
          </p>
          <div className="brand-logo__actions">
            <button
              type="button"
              className="brand-logo__button brand-logo__button--danger"
              onClick={handleRemoveConfirmed}
            >
              Yes, remove
            </button>
            <button
              type="button"
              className="brand-logo__button"
              onClick={() => setIsConfirmingRemoval(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <p className="brand-logo__hint">
        PNG only, up to {MAX_ASSET_BYTES / 1024}KB and {MAX_ASSET_PIXELS}px on either
        axis.
      </p>
      <p className="brand-logo__hint">
        Uploads go to the draft and are not visible to players until you publish.
      </p>

      {rejection !== null && (
        <p className="brand-logo__error" role="alert">
          {rejection}
        </p>
      )}
    </section>
  );
}
